import React from "react";
import { Helmet } from 'react-helmet';
import "./TermsConditions.css";
import { useNavigate } from "react-router-dom";


function TermsConditions() {
  const navigate = useNavigate();


  return (
    <div className="terms-container">
      <Helmet>
        <title>Terms & Conditions | VaahanBuddy - Vaahan Services in India</title>
        <meta name="description" content="Read VaahanBuddy's Terms & Conditions for using our vaahan services platform in India. Understand service model, user rights, and legal obligations." />
        <meta name="keywords" content="VaahanBuddy terms, conditions, vaahan services, India, RTO services, user agreement, legal terms" />
        <meta property="og:title" content="Terms & Conditions | VaahanBuddy" />
        <meta property="og:description" content="VaahanBuddy's Terms & Conditions governing the use of our vaahan services platform in India." />
        <meta property="og:image" content="https://rtoxpert.com/assets/logo.png" />
        <meta property="og:url" content="https://rtoxpert.com/terms-conditions" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="terms-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Terms & Conditions</h1>
      </div>


      <div className="terms-content">
        <section>
          <h2>Introduction</h2>
          <p>
            These Terms and Conditions govern your use of VaahanBuddy - Vaahan Services Assistant for India, a private platform that helps users with vaahan-related tasks in India via independent agents. By accessing or using our application, you agree to be bound by these Terms. If you do not agree with any part of these Terms, please do not use our platform or services.
          </p>
          <p>
            VaahanBuddy simplifies access to vaahan services, document management, and basic information by connecting users to knowledgeable agents who follow standard public procedures. Our platform facilitates various vaahan-related services through independent agents who assist with RTO services and other vaahan-related tasks.
          </p>
          <div className="important-disclaimer">
            <h3>🔒 Important Disclaimer:</h3>
            <p>
              <strong>VaahanBuddy is NOT affiliated with any government entity, RTO office, or government authority.</strong> All services are independently provided by third-party agents who follow standard public procedures. We do not represent any government body, do not have government authorization, and do not provide direct government services.
            </p>
          </div>
        </section>


        <section>
          <h2>Platform Overview & Service Model</h2>
          <p>
            VaahanBuddy operates as a service facilitation platform with the following key features:
          </p>
          <ul>
            <li><strong>Account Creation:</strong> Register using only your phone number with OTP-based login system.</li>
            <li><strong>Digital Garage:</strong> Securely store vaahan-related documents in our private MongoDB database with full user control.</li>
            <li><strong>RC & Challan Information:</strong> Data fetched from <a href="https://gridlines.io/products/rc-api">Gridline.io</a>, a licensed third-party API accessing public RTO records.</li>
            <li><strong>Independent Agent Services:</strong> Submit service requests for NOC, ownership transfer, address change, etc. Our independent agents contact you and guide you through standard procedures.</li>
            <li><strong>Vaahan Buy/Sell:</strong> Redirects to certified platforms such as Spinny and Cars24.</li>
            <li><strong>Accessories:</strong> External links to platforms like Flipkart for vaahan accessories.</li>
            <li><strong>Nearby Services:</strong> View nearby police stations, mechanics, etc., using Google Maps API (location accessed only when used).</li>
            <li><strong>DL Test Preparation:</strong> Practice questions for driving license tests with additional preparation materials available on request.</li>
          </ul>
        </section>


        <section>
          <h2>How Our Service Process Works</h2>
          <p>
            Understanding our operational model is essential for using our services:
          </p>
          <ol>
            <li><strong>Service Request:</strong> Users fill in their vaahan or service request details through the app.</li>
            <li><strong>Agent Assignment:</strong> Our independent agents reach out via phone or email to assist you.</li>
            <li><strong>Standard Procedure Guidance:</strong> Agents follow standard public procedures (forms, documentation) — no government authorization is used or required.</li>
            <li><strong>User Authorization:</strong> You authorize agents to assist with form submissions and provide guidance on your behalf.</li>
            <li><strong>Service Completion:</strong> Services are completed through standard channels with agent support and guidance.</li>
          </ol>
        </section>


        <section>
          <h2>Account Creation & Data Requirements</h2>
          <p>
            VaahanBuddy has minimal data requirements for account creation:
          </p>
          <ul>
            <li><strong>Required Information:</strong> Only your phone number for OTP-based login and account creation.</li>
            <li><strong>Optional Information:</strong> Name, email address, profile picture, address, and vaahan information — all provided voluntarily for enhanced service experience.</li>
            <li><strong>User Control:</strong> You can delete any document or data at any time directly from the app.</li>
            <li><strong>Age Requirement:</strong> You must be at least 18 years old or have legal guardian consent to use our services.</li>
            <li><strong>Data Storage:</strong> All data is securely stored in our private MongoDB database, not accessible to the public.</li>
          </ul>
        </section>


        <section>
          <h2>Use of Our Services</h2>
          <p>
            By using VaahanBuddy, you agree to the following conditions:
          </p>
          <ul>
            <li><strong>Accurate Information:</strong> You must provide accurate, complete, and up-to-date information when creating an account or requesting services.</li>
            <li><strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</li>
            <li><strong>Lawful Use:</strong> You agree not to use our services for any illegal, unauthorized, or fraudulent purposes, including submitting false or misleading information.</li>
            <li><strong>Agent Authorization:</strong> You acknowledge and authorize our independent agents to assist you with service requests, provide guidance, and help with form submissions on your behalf.</li>
            <li><strong>Standard Procedures:</strong> You understand that our agents provide assistance through standard public procedures and educational materials (such as road signs for license applications) without requiring government approval or affiliation.</li>
            <li><strong>Third-Party Platforms:</strong> When redirected to external platforms (Spinny, Cars24, Flipkart), you acknowledge that those platforms operate under their own terms and conditions.</li>
            <li><strong>Location Services:</strong> Location data is accessed only when you specifically use the "Nearby Services" feature and is not stored permanently.</li>
          </ul>
        </section>


        <section>
          <h2>Technical Infrastructure & Security</h2>
          <p>
            VaahanBuddy maintains robust technical infrastructure:
          </p>
          <ul>
            <li><strong>In-House Development:</strong> All service-related APIs are developed in-house and hosted securely on AWS EC2 instances.</li>
            <li><strong>Data Security:</strong> No third-party has access to our infrastructure. APIs handle user registration, document upload, service request handling, and communication between users and agents.</li>
            <li><strong>Secure Storage:</strong> Data is stored in our private MongoDB database with industry-standard encryption and security measures.</li>
            <li><strong>User Control:</strong> Users maintain full control over their data and can delete any information at any time.</li>
          </ul>
        </section>


        <section>
          <h2>Service Fees and Payments</h2>
          <p>
            Certain services on VaahanBuddy may incur fees. The following terms apply:
          </p>
          <ul>
            <li><strong>Transparent Pricing:</strong> All fees, including service charges and applicable taxes, will be clearly displayed before you confirm a transaction.</li>
            <li><strong>Secure Payments:</strong> We use secure third-party payment gateways to process transactions.</li>
            <li><strong>Refunds:</strong> Refunds, if applicable, will be processed in accordance with our Refund Policy, available on our platform.</li>
            <li><strong>Fee Changes:</strong> We reserve the right to modify fees at any time, with notice provided for significant changes.</li>
            <li><strong>Additional Charges:</strong> You are responsible for any additional government fees or charges imposed by RTO offices or other authorities during service processing.</li>
            <li><strong>Agent Services:</strong> Fees for agent assistance and guidance services will be clearly communicated before service commencement.</li>
          </ul>
        </section>


        <section>
          <h2>User Content & Document Management</h2>
          <p>
            When you use our Digital Garage and upload content:
          </p>
          <ul>
            <li><strong>License Grant:</strong> You grant VaahanBuddy and our agents a non-exclusive, worldwide, royalty-free license to use, store, and process your content solely for providing our services.</li>
            <li><strong>User Responsibility:</strong> You are solely responsible for the accuracy, legality, and completeness of any content you provide, including vaahan documents and personal information.</li>
            <li><strong>Voluntary Data:</strong> All personal data beyond your phone number is provided voluntarily for enhanced service experience and is not compulsory.</li>
            <li><strong>Data Control:</strong> You can delete any uploaded document or data at any time directly from the app.</li>
            <li><strong>No Liability:</strong> VaahanBuddy is not liable for errors or discrepancies in user-provided content.</li>
          </ul>
        </section>


        <section>
          <h2>Third-Party Services and Data Sources</h2>
          <p>
            VaahanBuddy integrates with various third-party services:
          </p>
          <ul>
            <li><strong>Gridline.io:</strong> We source vaahan RC and challan information through <a href="https://gridlines.io/products/rc-api">Gridline.io</a>, a licensed third-party service accessing publicly available government RTO data. We are not liable for errors, omissions, or discrepancies in this data.</li>
            <li><strong>External Platforms:</strong> Integration with vaahan platforms (Spinny, Cars24) and shopping platforms (Flipkart). These services are governed by their own terms and conditions.</li>
            <li><strong>Google Maps API:</strong> Used for the "Nearby Services" feature when specifically accessed by users.</li>
            <li><strong>Data Verification:</strong> You are responsible for verifying critical information directly with relevant RTO offices or authorities.</li>
            <li><strong>Platform Responsibility:</strong> We are not responsible for the content, practices, or performance of third-party services.</li>
          </ul>
        </section>


        <section>
          <h2>Intellectual Property</h2>
          <p>
            All content, features, and functionality of the VaahanBuddy platform, including but not limited to text, graphics, logos, software, and proprietary APIs, are owned by VaahanBuddy or its licensors and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
          </p>
          <p>
            You may not reproduce, distribute, modify, or create derivative works from our platform or its content without prior written consent from VaahanBuddy.
          </p>
        </section>


        <section>
          <h2>Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, VaahanBuddy, its independent agents, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use our services, including but not limited to:
          </p>
          <ul>
            <li>Errors in government data sourced through Gridline.io</li>
            <li>Delays in service processing by government authorities</li>
            <li>Issues with third-party services or platforms</li>
            <li>Inaccuracies in publicly available RTO data</li>
            <li>Technical issues with external APIs or services</li>
          </ul>
          <p>
            Our liability is limited to the amount paid for the specific service in question, where applicable, and we are not responsible for any additional costs incurred due to delays or errors beyond our control.
          </p>
        </section>


        <section>
          <h2>Indemnification</h2>
          <p>
            You agree to indemnify and hold VaahanBuddy, its independent agents, affiliates, and employees harmless from any claims, losses, liabilities, expenses, damages, or costs, including reasonable attorneys' fees, arising from:
          </p>
          <ul>
            <li>Your violation of these Terms</li>
            <li>Submission of inaccurate or illegal content</li>
            <li>Misuse of our services or platform</li>
            <li>Unauthorized use of third-party services through our platform</li>
            <li>Any actions taken by our agents on your authorized behalf</li>
          </ul>
        </section>


        <section>
          <h2>Termination</h2>
          <p>
            <strong>Our Rights:</strong> We reserve the right to suspend or terminate your access to VaahanBuddy at our discretion, without notice, if you violate these Terms or engage in activities that harm our platform, other users, or our agents.
          </p>
          <p>
            <strong>Your Rights:</strong> You may terminate your account at any time by contacting us or deleting your data through the app, subject to any pending service obligations.
          </p>
          <p>
            <strong>Data Deletion:</strong> Upon termination, you can delete all your data from our systems using the app's built-in deletion features.
          </p>
        </section>


        <section>
          <h2>Changes to Terms</h2>
          <p>
            We may update these Terms and Conditions from time to time to reflect changes in our services, legal requirements, or operational procedures. We will notify you of significant changes by posting the updated Terms on our platform and updating the effective date.
          </p>
          <p>
            Your continued use of VaahanBuddy after such changes constitutes your acceptance of the revised Terms.
          </p>
        </section>


        <section>
          <h2>Governing Law & Dispute Resolution</h2>
          <p>
            These Terms and Conditions are governed by the laws of India. Any disputes arising from or relating to these Terms or your use of VaahanBuddy shall be subject to the exclusive jurisdiction of the courts in Gorakhpur, Uttar Pradesh, India.
          </p>
        </section>


        <section>
          <h2>Contact Us</h2>
          <p>
            If you have any questions about these Terms and Conditions, please contact us:
          </p>
          <ul className="contact-list">
            <li><strong>📧 Email:</strong> hello@vaahanbuddy.com</li>
            <li><strong>📞 Phone:</strong> +91 95544 94415</li>
            <li>
              <strong>🏢 Address:</strong> Shop No - 42 First Floor, GDA Commercial Complex, Buddha
              Vihar Part-A, Taramandal Gorakhpur, U.P. - 273016
            </li>
          </ul>
          <p>
            <strong>🔗 Additional Resources:</strong>
          </p>
          <ul className="contact-list">
            <li><a href="https://www.vaahanbuddy.com/privacy-policy">Privacy Policy</a></li>
            <li><a href="https://www.vaahanbuddy.com/terms-conditions">Terms of Use</a></li>
          </ul>
        </section>
      </div>
    </div>
  );
}


export default TermsConditions;
